// src/components/ui/Dropdown/Dropdown.jsx
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, X, Search } from 'lucide-react';
import styles from './Dropdown.module.css';

const Dropdown = ({
  value,
  onChange,
  options = [],
  displayKey = 'label',
  valueKey = 'value',
  placeholder = 'Select...',
  disabled = false,
  icon = null,
  allowClear = true,
  className = '',
  panelClassName = '',
  size = 'default',
  dropdownPosition = 'bottom',
  searchable = false,
  searchPlaceholder = 'Search...',
  // Remote search (optional): when set, typing calls onSearch(term) (debounced) instead of
  // filtering `options` locally; the caller replaces `options` with the results.
  onSearch,
  loading = false,
  loadingText = 'Loading...',
  noOptionsText = 'No options',
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const searchTimerRef = useRef(null);
  const isRemoteSearch = typeof onSearch === 'function';
  const showSearch = searchable || isRemoteSearch;
  const [panelStyle, setPanelStyle] = useState(null);
  const [opensAbove, setOpensAbove] = useState(dropdownPosition === 'top');
  const wrapperRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        wrapperRef.current && !wrapperRef.current.contains(e.target) &&
        panelRef.current && !panelRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Position the portaled panel against the trigger, keeping it anchored while
  // any ancestor (e.g. a modal body) scrolls or the window resizes.
  useEffect(() => {
    if (!open) return;

    // Inside a fixed layer (modal, header, fixed action bar) the panel must stack above it;
    // on the normal page it stays below the sticky top header (z-index 1000) while scrolling.
    let inFixedLayer = false;
    for (let el = wrapperRef.current; el && el !== document.body; el = el.parentElement) {
      if (getComputedStyle(el).position === 'fixed') { inFixedLayer = true; break; }
    }
    const zIndex = inFixedLayer ? 1200 : 999;

    const updatePosition = () => {
      if (!wrapperRef.current) return;
      const rect = wrapperRef.current.getBoundingClientRect();
      // Flip above when the panel doesn't fit below and there's more room above (same idea as DatePicker).
      // scrollHeight is the natural height even while maxHeight is capping the panel.
      const panelHeight = panelRef.current?.scrollHeight || 300;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const placeAbove = dropdownPosition === 'top' || (spaceBelow < panelHeight + 6 && spaceAbove > spaceBelow);
      // Cap to the chosen side so the whole panel stays on screen when neither side fits.
      const maxHeight = Math.max(120, (placeAbove ? spaceAbove : spaceBelow) - 12);
      setOpensAbove(placeAbove);
      setPanelStyle(
        placeAbove
          ? { position: 'fixed', left: rect.left, right: 'auto', width: rect.width, top: 'auto', bottom: window.innerHeight - rect.top + 6, maxHeight, zIndex }
          : { position: 'fixed', left: rect.left, right: 'auto', width: rect.width, top: rect.bottom + 6, bottom: 'auto', maxHeight, zIndex }
      );
    };

    updatePosition();
    // Recompute once the panel has rendered and its real height is known.
    const raf = requestAnimationFrame(updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, dropdownPosition]);

  useEffect(() => {
    if (!open) setSearchTerm('');
  }, [open]);

  useEffect(() => () => clearTimeout(searchTimerRef.current), []);

  const handleSearchChange = (term) => {
    setSearchTerm(term);
    if (!isRemoteSearch) return;
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => onSearch(term), 300);
  };

  const selectedOption = options.find(
    (opt) => String(opt[valueKey]) === String(value)
  );

  const visibleOptions = !isRemoteSearch && searchable && searchTerm
    ? options.filter((opt) => String(opt[displayKey]).toLowerCase().includes(searchTerm.toLowerCase()))
    : options;

  const handleSelect = (option) => {
    onChange(option[valueKey]);
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
  };

  return (
    <div
      ref={wrapperRef}
      className={`${styles.wrapper} ${className} ${disabled ? styles.disabled : ''}`}
    >
      <div
        className={`${styles.trigger} ${size === 'compact' ? styles.triggerCompact : ''} ${open ? styles.triggerOpen : ''}`}
        onClick={() => !disabled && setOpen((o) => !o)}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); !disabled && setOpen((o) => !o); } }}
      >
        {icon && <span className={styles.triggerIcon}>{icon}</span>}
        <span className={`${styles.triggerText} ${!selectedOption ? styles.placeholder : ''}`}>
          {selectedOption ? selectedOption[displayKey] : placeholder}
        </span>
        {allowClear && !disabled && selectedOption && (
          <button type="button" className={styles.clearButton} onClick={handleClear} title="Clear">
            <X size={14} />
          </button>
        )}
        <ChevronDown className={styles.chevronIcon} size={16} />
      </div>

      {open && panelStyle && createPortal(
        <div
          ref={panelRef}
          className={`${styles.dropdown} ${opensAbove ? styles.dropdownTop : ''} ${panelClassName}`}
          style={panelStyle}
        >
          {showSearch && (
            <div className={styles.searchWrapper}>
              <Search className={styles.searchIcon} size={16} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder={searchPlaceholder}
                className={styles.searchInput}
                autoFocus
              />
              {searchTerm && (
                <button type="button" className={styles.clearSearch} onClick={() => handleSearchChange('')}>
                  <X size={14} />
                </button>
              )}
            </div>
          )}
          <div className={styles.optionsList}>
            {loading ? (
              <div className={styles.emptyState}>{loadingText}</div>
            ) : visibleOptions.length > 0 ? (
              visibleOptions.map((option) => {
                const isSelected = String(option[valueKey]) === String(value);
                return (
                  <button
                    key={option[valueKey]}
                    type="button"
                    className={`${styles.option} ${isSelected ? styles.optionSelected : ''}`}
                    onClick={() => handleSelect(option)}
                  >
                    <span className={styles.optionLabel}>{option[displayKey]}</span>
                    {isSelected && <Check size={14} className={styles.checkIcon} />}
                  </button>
                );
              })
            ) : (
              <div className={styles.emptyState}>{noOptionsText}</div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Dropdown;
